// File Metadata:
// Filename: ShipmentLogDataGrid.js
// Version: 1.00
// Created: 2025-03-24 21:59:27
// Last Modified: 2025-04-01 16:55:19
// Authors: [Your Name]
// Description: A brief summary of what this file does

import './datatable.scss'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useGetShipmentlogsQuery } from '../api/shipmentlogsApiSlice'
import useAuth from '../../hooks/useAuth'
import { format } from 'date-fns'
import { Box, FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme, Button, Grid, TextField, InputAdornment, Typography, Paper, Chip, Badge, Collapse, IconButton, Stack } from '@mui/material'
import { CircularProgress } from '@mui/material'
import { TYPES } from '../../config/constants'
import { SHIPMENT_STATUS, PRIORITY_LEVELS } from '../../config/shipmentConstants'
import { Search as SearchIcon } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import ShipmentMetrics from './components/ShipmentMetrics'

const ShipmentLogDataGrid = () => {
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    const { username, isManager, isAdmin } = useAuth()
    const navigate = useNavigate()

    const {
        data: shipmentlogs,
        isLoading,
        isSuccess,
        isError,
        error
    } = useGetShipmentlogsQuery('shipmentlogsList', {
        pollingInterval: 15000,
        refetchOnFocus: true,
        refetchOnMountOrArgChange: true
    })

    const [typeFilter, setTypeFilter] = useState('ALL')
    const [searchQuery, setSearchQuery] = useState('')
    const [sortModel, setSortModel] = useState([
        {
            field: 'createdAt',
            sort: 'desc',
        },
    ])
    const [statusFilter, setStatusFilter] = useState('ALL')
    const [priorityFilter, setPriorityFilter] = useState('ALL')
    const [filtersExpanded, setFiltersExpanded] = useState(false)

    const handleToggleFilters = () => {
        setFiltersExpanded(!filtersExpanded)
    }

    const userColumns = [
        {
            field: 'createdAt',
            headerName: 'Created At',
            flex: 2,
            renderCell: params => {
                return format(new Date(params.row.createdAt), 'dd-MM-yyyy HH:mm')
            }
        },
        {
            field: 'sscc',
            headerName: 'SSCC',
            flex: 2
        },
        {
            field: 'type',
            headerName: 'Type',
            flex: 2
        },
        {
            field: 'tour',
            headerName: 'Tour',
            flex: 1
        },
        {
            field: 'images',
            headerName: 'Images',
            flex: 1,
            renderCell: params => params.row.img?.length || 0
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <Chip 
                    label={params.value}
                    color={SHIPMENT_STATUS[params.value]?.color || 'default'}
                    size="small"
                />
            )
        },
        {
            field: 'priority',
            headerName: 'Priority',
            flex: 1,
            renderCell: (params) => (
                <Chip 
                    label={params.value}
                    color={PRIORITY_LEVELS[params.value]?.color || 'default'}
                    size="small"
                />
            )
        },
        {
            field: 'assignedTo',
            headerName: 'Assigned To',
            flex: 2,
            renderCell: (params) => (
                <Chip 
                    label={params.row.assignedTo?.username || 'Unassigned'}
                    color='primary'
                    size="small"
                />
            )
        }
    ]

    const mobileColumns = [
        {
            field: 'createdAt',
            headerName: 'Date',
            width: 80,
            renderCell: params => format(new Date(params.row.createdAt), 'dd/MM/yy')
        },
        {
            field: 'sscc',
            headerName: 'SSCC',
            width: 150
        },
        {
            field: 'tour',
            headerName: 'Tour',
            flex: 1
        },
        {
            field: 'type',
            headerName: 'Type',
            width: 80
        }
    ]

    const filterAndSortShipmentlogs = (shipmentlogs) => {
        if (!shipmentlogs?.ids?.length) return []
        
        let filteredIds = [...shipmentlogs.ids]
        
        if (typeFilter !== 'ALL') {
            filteredIds = filteredIds.filter(id => 
                shipmentlogs.entities[id].type === typeFilter
            )
        }

        // Add search filter
        if (searchQuery) {
            filteredIds = filteredIds.filter(id => {
                const record = shipmentlogs.entities[id]
                return record.sscc?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                       record.tour?.toLowerCase().includes(searchQuery.toLowerCase())
            })
        }

        // Add status filter
        if (statusFilter !== 'ALL') {
            filteredIds = filteredIds.filter(id => 
                shipmentlogs.entities[id].status === statusFilter
            )
        }

        // Add priority filter
        if (priorityFilter !== 'ALL') {
            filteredIds = filteredIds.filter(id => 
                shipmentlogs.entities[id].priority === priorityFilter
            )
        }

        return filteredIds
    }

    // Define typeFilterOptions before using it in the components
    const typeFilterOptions = [
        { taskId: 'ALL', text: 'All Types' },
        ...TYPES.tasklog
    ]

    const desktopFilters = (
        <Box sx={{ 
            display: { xs: 'none', md: 'flex' }, 
            gap: 2,
            alignItems: 'center',
            mb: 2
        }}>
            <FormControl sx={{ width: 200 }}>
                <InputLabel>Type</InputLabel>
                <Select
                    value={typeFilter}
                    label="Type"
                    onChange={(e) => setTypeFilter(e.target.value)}
                >
                    {typeFilterOptions.map(type => (
                        <MenuItem key={type.taskId} value={type.taskId}>
                            {type.text}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <TextField
                placeholder="Search SSCC/Tour"
                variant="outlined"
                size="medium"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ width: 200 }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon />
                        </InputAdornment>
                    ),
                }}
            />

            <Box sx={{ flex: 1 }}>
                <ShipmentMetrics />
            </Box>

            <Button
                variant="contained"
                color="primary"
                component={Link}
                to="/dash/shipments/new"
                sx={{ height: '56px', whiteSpace: 'nowrap' }}
            >
                Add New
            </Button>
        </Box>
    )

    const mobileFilters = (
        <Box sx={{ 
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            gap: 2,
            mb: 2
        }}>
            <Paper sx={{ width: '100%' }}>
                <Stack 
                    direction="row" 
                    justifyContent="space-between" 
                    alignItems="center"
                    onClick={handleToggleFilters}
                    sx={{ 
                        cursor: 'pointer', 
                        p: 2,
                        borderBottom: filtersExpanded ? 1 : 0,
                        borderColor: 'divider'
                    }}
                >
                    <Typography>Filters & Metrics</Typography>
                    <IconButton>
                        {filtersExpanded ? <KeyboardArrowUpIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Stack>

                <Collapse in={filtersExpanded}>
                    <Box sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={typeFilter}
                                    label="Type"
                                    onChange={(e) => setTypeFilter(e.target.value)}
                                >
                                    {typeFilterOptions.map(type => (
                                        <MenuItem key={type.taskId} value={type.taskId}>
                                            {type.text}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                placeholder="Search SSCC/Tour"
                                variant="outlined"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <ShipmentMetrics />
                        </Stack>
                    </Box>
                </Collapse>
            </Paper>

            <Button
                fullWidth
                variant="contained"
                color="primary"
                component={Link}
                to="/dash/shipments/new"
                sx={{ height: '56px' }}
            >
                Add New
            </Button>
        </Box>
    )

    let content

    if (isLoading) {
        return <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
        </Box>
    }

    if (isError) {
        content = <p className='errmsg'>{error?.data?.message}</p>
    }

    if (isSuccess) {
        const filteredIds = filterAndSortShipmentlogs(shipmentlogs)
        const tableContent = filteredIds.map(id => shipmentlogs.entities[id])

        content = (
            <Box 
				sx={{ 
					height: 600,
					width: '100%',
					p: 0,
					'& .MuiDataGrid-root': { border: 'none' }
				}}
			>
                <Typography variant="h6" sx={{ 
					color: 'gray',
					order: isMobile ? 0 : 1  // Title first on mobile, after filters on desktop
				}}>
                    Shipment Logs
                </Typography>

                {desktopFilters}
                {mobileFilters}

                <Box sx={{ 
                    width: '100%',
                    height: {
                        xs: 'calc(100vh - 200px)', // Mobile height
                        sm: 600 // Desktop height
                    },
                    '& .MuiDataGrid-root': {
                        border: 'none',
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f0f0f0'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: 'background.paper',
                            borderBottom: '2px solid #f0f0f0'
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            backgroundColor: 'background.paper'
                        }
                    }
                }}>
                    <DataGrid
                        className='datagrid'
                        rows={tableContent}
                        columns={isMobile ? mobileColumns : userColumns}
                        rowsPerPageOptions={isMobile ? [10, 25] : [25, 50, 100]}
                        components={{
                            Toolbar: isMobile ? undefined : GridToolbar
                        }}
                        density={isMobile ? "compact" : "standard"}
                        disableColumnFilter={isMobile}
                        disableColumnMenu={isMobile}
                        onRowClick={(params) => navigate(`/dash/shipments/${params.id}`)}
                        sortModel={sortModel}
                        onSortModelChange={(model) => setSortModel(model)}
                        autoHeight={isMobile} // Add this
                        pageSize={isMobile ? 10 : 25} // Add this
                        sx={{
                            // Add mobile-specific styles
                            '& .MuiDataGrid-virtualScroller': {
                                minHeight: isMobile ? '200px' : 'auto'
                            },
                            '& .MuiDataGrid-cell': {
                                padding: isMobile ? '8px 4px' : '16px'
                            },
                            '& .MuiDataGrid-columnHeader': {
                                padding: isMobile ? '8px 4px' : '16px'
                            }
                        }}
                    />
                </Box>
            </Box>
        )
    }

    return content
}

export default ShipmentLogDataGrid
